import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import usePageTitle from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Users, Gavel, Eye, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  {
    value: 'club_coach',
    label: 'Club / Coach',
    icon: Users,
    description: 'You have used Scorz to enter a competition, or follow your gymnasts scores at the competition',
  },
  {
    value: 'judge',
    label: 'Judge',
    icon: Gavel,
    description: 'You have used the Scorz Terminals to enter scores for gymnasts at the competition',
  },
  {
    value: 'spectator',
    label: 'Spectator',
    icon: Eye,
    description: 'You watch competitions and follow the public live scores',
  },
];

const STEP_ROLE = 'role';
const STEP_DETAILS = 'details';
const STEP_DONE = 'done';

export default function FeedbackPage() {
  usePageTitle('Feedback');

  const [step, setStep] = useState(STEP_ROLE);
  const [role, setRole] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [likes, setLikes] = useState('');
  const [dislikes, setDislikes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selectedRole = ROLES.find(r => r.value === role);

  const handleRoleSelect = (value) => {
    setRole(value);
    // Small delay so users see the selection state before advancing
    setTimeout(() => setStep(STEP_DETAILS), 120);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!likes.trim() && !dislikes.trim()) {
      toast.error('Please share at least one thing you like or dislike');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/feedback`, {
        role,
        name: name.trim() || null,
        email: email.trim() || null,
        likes: likes.trim(),
        dislikes: dislikes.trim(),
      });
      setStep(STEP_DONE);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Failed to submit: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(STEP_ROLE);
    setRole('');
    setName('');
    setEmail('');
    setLikes('');
    setDislikes('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="py-8 flex justify-center">
        <img src="/scorz-logo.png" alt="Scorz" className="h-14" />
      </header>

      <main className="flex-1 flex justify-center px-4 pb-16">
        <div className="w-full max-w-2xl">
          {/* ---------- STEP 1: pick role ---------- */}
          {step === STEP_ROLE && (
            <Card className="bg-white">
              <CardContent className="pt-8 pb-8 space-y-6">
                <div className="text-center">
                  <h1 className="text-2xl font-bold" data-testid="feedback-heading">
                    Before we begin…
                  </h1>
                  <p className="text-gray-600 mt-1 text-sm">
                    Please select whether you're a club/coach, judge, or spectator.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {ROLES.map((r) => {
                    const Icon = r.icon;
                    const active = role === r.value;
                    return (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => handleRoleSelect(r.value)}
                        className={`text-left p-5 rounded-xl border-2 transition-all flex items-start gap-4 ${
                          active
                            ? 'border-black bg-black text-white shadow-md scale-[0.99]'
                            : 'border-gray-200 bg-white hover:border-gray-400 hover:shadow-sm'
                        }`}
                        data-testid={`feedback-role-${r.value}`}
                      >
                        <div className={`mt-0.5 p-2 rounded-lg ${active ? 'bg-white/10' : 'bg-gray-100'}`}>
                          <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-gray-700'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-base">{r.label}</div>
                          <div className={`text-xs mt-1 leading-relaxed ${active ? 'text-gray-300' : 'text-gray-500'}`}>
                            {r.description}
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 self-center shrink-0 ${active ? 'text-white' : 'text-gray-300'}`} />
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ---------- STEP 2: details ---------- */}
          {step === STEP_DETAILS && selectedRole && (
            <form onSubmit={handleSubmit}>
              <Card className="bg-white">
                <CardContent className="pt-6 pb-8 space-y-6">
                  <button
                    type="button"
                    onClick={() => setStep(STEP_ROLE)}
                    className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
                    data-testid="feedback-back-btn"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    Back
                  </button>

                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-semibold">
                      <selectedRole.icon className="w-3.5 h-3.5" />
                      {selectedRole.label}
                    </div>
                    <h1 className="text-2xl font-bold mt-3">We'd love your feedback</h1>
                    <p className="text-gray-600 mt-1 text-sm">
                      Tell us what you like and what could be better.
                    </p>
                  </div>

                  {/* Optional contact fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="fb-name" className="text-sm">Name <span className="text-gray-400 font-normal">(optional)</span></Label>
                      <Input
                        id="fb-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="mt-1"
                        data-testid="feedback-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="fb-email" className="text-sm">Email <span className="text-gray-400 font-normal">(optional)</span></Label>
                      <Input
                        id="fb-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="mt-1"
                        data-testid="feedback-email-input"
                      />
                    </div>
                  </div>

                  {/* Likes */}
                  <div>
                    <Label htmlFor="fb-likes" className="text-sm font-semibold text-green-700">
                      👍 What do you <span className="underline">like</span>?
                    </Label>
                    <Textarea
                      id="fb-likes"
                      value={likes}
                      onChange={(e) => setLikes(e.target.value)}
                      placeholder="Anything that made your experience better…"
                      className="mt-1 min-h-[110px]"
                      data-testid="feedback-likes-input"
                    />
                  </div>

                  {/* Dislikes */}
                  <div>
                    <Label htmlFor="fb-dislikes" className="text-sm font-semibold text-red-700">
                      👎 What do you <span className="underline">not</span> like?
                    </Label>
                    <Textarea
                      id="fb-dislikes"
                      value={dislikes}
                      onChange={(e) => setDislikes(e.target.value)}
                      placeholder="Bugs, missing features, confusing moments…"
                      className="mt-1 min-h-[110px]"
                      data-testid="feedback-dislikes-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 bg-black hover:bg-gray-800 text-white text-base font-semibold"
                    data-testid="feedback-submit-btn"
                  >
                    {submitting ? 'Sending…' : 'Send feedback'}
                  </Button>

                  <p className="text-[11px] text-gray-400 text-center">
                    Your feedback is emailed directly to the Scorz team at admin@scorz.co.uk.
                  </p>
                </CardContent>
              </Card>
            </form>
          )}

          {/* ---------- STEP 3: thank you ---------- */}
          {step === STEP_DONE && (
            <Card className="border-green-200 bg-white">
              <CardContent className="pt-10 pb-10 text-center space-y-4">
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
                <h2 className="text-2xl font-bold">Thank you!</h2>
                <p className="text-gray-600">
                  We greatly appreciate your feedback, and this will be reviewed by the team.
                </p>
                <Button
                  onClick={resetForm}
                  variant="outline"
                  className="mt-4"
                  data-testid="feedback-send-another-btn"
                >
                  Send another
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
